import * as Yup from "yup";
import {
  noEmojis,
  sanitizationTest,
  sanitizeString,
} from "src/util/validationSchemas";

/* ------------------------------ status enums ------------------------------ */

export const FLEET_OPERATOR_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
];

export const FLEET_ENTITY_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const TARIFF_TYPES = {
  FLAT: "FLAT",
  AC_DC: "AC_DC",
  SLAB: "SLAB",
};

export const TARIFF_TYPE_OPTIONS = [
  { value: TARIFF_TYPES.FLAT, label: "Flat" },
  { value: TARIFF_TYPES.AC_DC, label: "AC / DC" },
  { value: TARIFF_TYPES.SLAB, label: "Slab" },
];

export const TARIFF_TYPE_LABELS = {
  [TARIFF_TYPES.FLAT]: "Flat",
  [TARIFF_TYPES.AC_DC]: "AC / DC",
  [TARIFF_TYPES.SLAB]: "Slab",
};

export const ACCESS_SCOPES = {
  COMPANY: "COMPANY",
  TEAM: "TEAM",
  CHARGER: "CHARGER",
};

export const ACCESS_SCOPE_OPTIONS = [
  { value: ACCESS_SCOPES.COMPANY, label: "Whole Company" },
  { value: ACCESS_SCOPES.TEAM, label: "Specific Team" },
  { value: ACCESS_SCOPES.CHARGER, label: "Specific Charger" },
];

export const CHARGER_POWER_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "AC", label: "AC" },
  { value: "DC", label: "DC" },
];

/* ------------------------------ small helpers ----------------------------- */

// Backend lists come back either as { items, total } or as a plain array.
export const toList = (data) =>
  data?.items ?? (Array.isArray(data) ? data : []);

export const listTotal = (data, fallbackLength = 0) =>
  Number(data?.total ?? fallbackLength);

export const getFleetOperatorId = (row) =>
  row?.fleet_operator_id ?? row?.fleetOperatorId ?? row?.id;

export const fullName = (row) =>
  [row?.first_name, row?.last_name].filter(Boolean).join(" ") || "-";

// Convert "" to null so optional columns are stored as NULL, not empty string.
export const emptyToNull = (obj) =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    acc[key] = value === "" ? null : value;
    return acc;
  }, {});

export const statusChipColor = (status) => {
  switch ((status ?? "").toLowerCase()) {
    case "active":
    case "captured":
    case "debited":
      return "success";
    case "suspended":
    case "pending":
      return "warning";
    case "inactive":
    case "failed":
      return "default";
    default:
      return "default";
  }
};

export const ledgerTypeChipColor = (type) => {
  switch ((type ?? "").toUpperCase()) {
    case "TOPUP":
      return "success";
    case "DEBIT":
      return "error";
    case "REFUND":
      return "info";
    case "ADJUSTMENT":
      return "warning";
    default:
      return "default";
  }
};

export const formatMoney = (amount, currency = "INR") => {
  if (amount === null || amount === undefined || amount === "") return "-";
  const value = Number(amount);
  if (Number.isNaN(value)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return `${currency ?? ""} ${value.toFixed(2)}`;
  }
};

export const formatKwh = (kwh) => {
  if (kwh === null || kwh === undefined || kwh === "") return "-";
  const value = Number(kwh);
  if (Number.isNaN(value)) return "-";
  return `${value.toFixed(2)} kWh`;
};

/* ------------------------------ default values ---------------------------- */

export const getFleetOperatorDefaultValues = () => ({
  name: "",
  legal_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  billing_email: "",
  fleet_type: "",
  external_ref: "",
  status: "active",
});

export const getDriverDefaultValues = () => ({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  license_number: "",
  license_expiry_date: "",
  status: "active",
});

export const getVehicleDefaultValues = () => ({
  registration_number: "",
  vin: "",
  make: "",
  model: "",
  year: "",
  vehicle_type: "",
  battery_capacity_kwh: "",
  status: "active",
});

export const getTariffDefaultValues = () => ({
  name: "",
  tariff_type: TARIFF_TYPES.FLAT,
  currency: "INR",
  tax_percent: 0,
  flat_price_per_unit: "",
  ac_price_per_unit: "",
  dc_price_per_unit: "",
  effective_from: "",
  effective_to: "",
  status: "active",
  slabs: [],
});

export const getEmptySlabRow = (minUnits = 0, sortOrder = 0) => ({
  min_units: minUnits,
  max_units: "",
  price_per_unit: "",
  charger_power_type: "",
  sort_order: sortOrder,
});

/* ---------------------------- validation schemas --------------------------- */

const safeString = (max, label) =>
  Yup.string()
    .max(max, `${label} must be ${max} characters or less`)
    .transform(sanitizeString)
    .test("sanitized", "Input contained invalid characters", sanitizationTest)
    .test("no-emojis", "Emojis are not allowed", noEmojis);

const optionalEmail = Yup.string()
  .email("Invalid Email")
  .test("no-emojis", "Emojis are not allowed", noEmojis);

export const fleetOperatorValidationSchema = Yup.object().shape({
  name: safeString(150, "Name").required("Operator name is required"),
  legal_name: safeString(200, "Legal name"),
  contact_name: safeString(150, "Contact name"),
  contact_email: optionalEmail,
  billing_email: optionalEmail,
  contact_phone: Yup.string()
    .matches(
      /^\+?[0-9]*$/,
      "Only numbers are allowed, + can only be at the beginning"
    )
    .max(20, "Must be 20 digits or less"),
  fleet_type: safeString(50, "Fleet type"),
  external_ref: safeString(100, "External reference"),
  status: Yup.string()
    .oneOf(["active", "inactive", "suspended"])
    .required("Status is required"),
});

export const fleetDriverValidationSchema = Yup.object().shape({
  first_name: safeString(80, "First name").required("First name is required"),
  last_name: safeString(80, "Last name").required("Last name is required"),
  email: optionalEmail,
  phone: Yup.string()
    .matches(
      /^\+?[0-9]*$/,
      "Only numbers are allowed, + can only be at the beginning"
    )
    .max(20, "Must be 20 digits or less"),
  license_number: safeString(80, "License number"),
  license_expiry_date: Yup.string(),
  status: Yup.string().oneOf(["active", "inactive"]).required(),
});

export const fleetVehicleValidationSchema = Yup.object().shape({
  registration_number: safeString(50, "Registration number").required(
    "Registration number is required"
  ),
  vin: safeString(100, "VIN"),
  make: safeString(80, "Make"),
  model: safeString(80, "Model"),
  year: Yup.number()
    .typeError("Year must be a number")
    .integer("Year must be a whole number")
    .min(1980, "Year looks too old")
    .max(2100, "Year looks too far in the future")
    .nullable()
    .transform((value, original) => (original === "" ? null : value)),
  vehicle_type: safeString(50, "Vehicle type"),
  battery_capacity_kwh: Yup.number()
    .typeError("Battery capacity must be a number")
    .moreThan(0, "Battery capacity must be greater than 0")
    .nullable()
    .transform((value, original) => (original === "" ? null : value)),
  status: Yup.string().oneOf(["active", "inactive"]).required(),
});

const optionalPrice = (label) =>
  Yup.number()
    .typeError(`${label} must be a number`)
    .min(0, `${label} cannot be negative`)
    .nullable()
    .transform((value, original) => (original === "" ? null : value));

export const fleetTariffValidationSchema = Yup.object().shape({
  name: safeString(150, "Name").required("Tariff name is required"),
  tariff_type: Yup.string()
    .oneOf(Object.values(TARIFF_TYPES))
    .required("Tariff type is required"),
  currency: safeString(10, "Currency"),
  tax_percent: Yup.number()
    .typeError("Tax percent must be a number")
    .min(0, "Tax percent cannot be negative")
    .max(100, "Tax percent cannot exceed 100"),
  flat_price_per_unit: Yup.number().when("tariff_type", {
    is: TARIFF_TYPES.FLAT,
    then: Yup.number()
      .typeError("Price per kWh must be a number")
      .moreThan(0, "Price per kWh must be greater than 0")
      .required("Price per kWh is required"),
    otherwise: optionalPrice("Price per kWh"),
  }),
  ac_price_per_unit: Yup.number().when("tariff_type", {
    is: TARIFF_TYPES.AC_DC,
    then: Yup.number()
      .typeError("AC price must be a number")
      .moreThan(0, "AC price must be greater than 0")
      .required("AC price is required"),
    otherwise: optionalPrice("AC price"),
  }),
  dc_price_per_unit: Yup.number().when("tariff_type", {
    is: TARIFF_TYPES.AC_DC,
    then: Yup.number()
      .typeError("DC price must be a number")
      .moreThan(0, "DC price must be greater than 0")
      .required("DC price is required"),
    otherwise: optionalPrice("DC price"),
  }),
  effective_from: Yup.string(),
  effective_to: Yup.string(),
  status: Yup.string().oneOf(["active", "inactive"]).required(),
});

export const walletTopupValidationSchema = Yup.object().shape({
  amount: Yup.number()
    .typeError("Amount must be a number")
    .moreThan(0, "Amount must be greater than 0")
    .required("Amount is required"),
  remarks: safeString(255, "Remarks"),
});

export const walletAdjustmentValidationSchema = Yup.object().shape({
  amount: Yup.number()
    .typeError("Amount must be a number")
    .notOneOf([0], "Amount cannot be zero")
    .required("Amount is required"),
  remarks: safeString(255, "Remarks").required("Remarks are required"),
});
